// ログイン中の職員（/me）と「いまの事業所」。選んだ事業所は localStorage に残し、API 呼び出しに付ける。
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from './api';
import { pickOffice } from './domain';
import { messageOf } from './errors';
import type { Me, MeOffice } from './types';

export const OFFICE_KEY = 'alfine.office';

interface OfficeState {
  me: Me | null;
  loading: boolean;
  error: string | null;
  officeId: string | null;
  office: MeOffice | null;
  selectOffice: (id: string) => void;
  reload: () => void;
}

const Ctx = createContext<OfficeState | null>(null);

function readStored(): string | null {
  try {
    return localStorage.getItem(OFFICE_KEY);
  } catch {
    return null;
  }
}

export function OfficeProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [officeId, setOfficeId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    // /me は事業所の文脈なしで呼ぶ（所属の一覧を得るため）
    api<Me>('/me')
      .then((m) => {
        setMe(m);
        setOfficeId(pickOffice(m, readStored()));
      })
      .catch((e) => setError(messageOf(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const selectOffice = useCallback((id: string) => {
    setOfficeId(id);
    try {
      localStorage.setItem(OFFICE_KEY, id);
    } catch {
      // 保存できなくてもこの画面では使える
    }
  }, []);

  const value = useMemo<OfficeState>(
    () => ({ me, loading, error, officeId, office: me?.principal.offices.find((o) => o.officeId === officeId) ?? null, selectOffice, reload: load }),
    [me, loading, error, officeId, selectOffice, load],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useOffice(): OfficeState {
  const v = useContext(Ctx);
  if (!v) throw new Error('OfficeProvider の外で useOffice が呼ばれました');
  return v;
}
